import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  Check,
  ChevronRight,
  LogIn,
  RefreshCw,
  Trophy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { RankingGroupImage } from '../components/rankings/RankingGroupImage';
import { CompetitionImage } from '../components/competitions/CompetitionImage';
import { useCachedData } from '../hooks/useCachedData';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { PushOptInDialog } from '../components/common/PushOptInDialog';
import { api } from '../services/api';
import { RANKING_LIMITS } from '../config/rankingLimits';
import { trackEvent } from '../utils/analytics';
import type { RankingSummary, RankingsListData, CompetitionsListData } from '../types';

function formatBRL(cents: number | null): string {
  return ((cents ?? 0) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
  });
}

// ── Pendências ───────────────────────────────────────────────────────────────

type TodoTone = 'urgent' | 'primary' | 'neutral';

interface Todo {
  key: string;
  title: string;
  detail: string;
  action: string;
  tone: TodoTone;
  onSelect: () => void;
  /** Shown instead of the position number by rows outside the count. */
  icon?: LucideIcon;
}

/** Ring colour marks the kind of ask; a filled pill means it costs you money or a commitment. */
const TONE: Record<TodoTone, { ring: string; action: string }> = {
  urgent: { ring: 'border-coral text-coral', action: 'gradient-sunset text-deep-blue' },
  primary: { ring: 'border-sunny text-sunny', action: 'gradient-sunset text-deep-blue' },
  neutral: {
    ring: 'border-turquoise text-turquoise',
    action: 'border border-turquoise/50 text-turquoise',
  },
};

function TodoRow({ todo, index }: { todo: Todo; index: number }) {
  const tone = TONE[todo.tone];
  const Icon = todo.icon;
  return (
    <button
      onClick={todo.onSelect}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-white/[.06] last:border-b-0 hover:bg-white/[.03] transition-colors"
    >
      <span
        className={`w-[26px] h-[26px] rounded-full border-2 text-[13px] font-bold flex items-center justify-center shrink-0 ${tone.ring}`}
      >
        {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={2.5} /> : index + 1}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-sm text-white truncate">{todo.title}</span>
        <span className="block text-xs text-white/55 truncate">{todo.detail}</span>
      </span>
      <span className={`text-xs font-bold px-3.5 py-2 rounded-full shrink-0 ${tone.action}`}>
        {todo.action}
      </span>
    </button>
  );
}

// ── Ranking card ─────────────────────────────────────────────────────────────

function RankingCard({ ranking, onOpen }: { ranking: RankingSummary; onOpen: () => void }) {
  const stats = [
    {
      label: 'posição',
      value: ranking.myPosition ? `${ranking.myPosition}º` : '—',
      accent: 'text-sunny',
    },
    { label: 'pontos', value: String(ranking.myPoints ?? 0), accent: 'text-white' },
    {
      label: 'rodadas',
      value: ranking.currentRoundNumber
        ? `${ranking.currentRoundNumber}${ranking.totalRounds ? `/${ranking.totalRounds}` : ''}`
        : '—',
      accent: 'text-white',
    },
  ];

  return (
    <button
      onClick={onOpen}
      className="w-full text-left glass-card rounded-2xl p-4 border-turquoise/30 hover:bg-white/[.06] transition-colors"
    >
      <div className="flex items-center gap-3 mb-3">
        <RankingGroupImage imageUrl={ranking.imageUrl} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-[15px] text-white truncate">{ranking.name}</p>
            {ranking.myRole === 'admin' && (
              <span className="shrink-0 text-[10px] font-bold text-turquoise bg-turquoise/15 px-2 py-0.5 rounded-md">
                Admin
              </span>
            )}
          </div>
          <p className="text-xs text-white/50 truncate">
            {ranking.subtitle} · {ranking.playerCount} jogador
            {ranking.playerCount === 1 ? '' : 'es'}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-white/35 shrink-0" />
      </div>
      <div className="flex gap-2">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex-1 bg-deep-blue/60 rounded-[10px] py-2.5 px-2 text-center"
          >
            <p className={`font-bold text-base leading-none ${stat.accent}`}>{stat.value}</p>
            <p className="text-[10px] text-white/50 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Início — the logged-in front door at `/`.
 *
 * Ordered by what it asks of you: what needs doing, then where you stand. The
 * pendências list gathers everything you are holding up — a round waiting on
 * your score, a group waiting on a wildcard, an entry fee waiting on payment —
 * plus the sign-up prompt for a ranking whose next draw you are not in.
 */
export function HubPage() {
  const navigate = useNavigate();
  const { subscriber, refreshSubscriber } = useAuth();

  const rankingsFetcher = useCallback(() => api.rankings.list(), []);
  const { data: rankingsData, isLoading, didRefresh, refetch } = useCachedData<RankingsListData>({
    cacheKey: 'bt_rankings_list',
    fetcher: rankingsFetcher,
  });

  const competitionsFetcher = useCallback(() => api.competitions.list(), []);
  const { data: competitionsData } = useCachedData<CompetitionsListData>({
    cacheKey: 'bt_competitions_list',
    fetcher: competitionsFetcher,
  });

  const push = usePushNotifications();
  const [showPushDialog, setShowPushDialog] = useState(false);

  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const rankings = rankingsData?.rankings ?? [];
  const competitions = competitionsData?.competitions ?? [];
  const firstName = subscriber?.name?.trim().split(' ')[0];

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([refetch(), refreshSubscriber()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetch, refreshSubscriber]);

  const handleJoin = () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    navigate(`/rankings/join/${code}`);
  };

  const activeRankings = rankings.filter((r) => r.status === 'active');

  const todos: Todo[] = [];

  for (const match of subscriber?.pendingRankingMatches ?? []) {
    todos.push({
      key: `match-${match.roundId}`,
      title: `Lançar placar · rodada ${match.roundNumber}`,
      detail: `${match.rankingName} · ${match.pendingMatchCount} partida${match.pendingMatchCount === 1 ? '' : 's'
        }`,
      action: 'Lançar',
      tone: 'neutral',
      onSelect: () => {
        trackEvent('btv_dashboard_pending_match_click', {
          rankingName: match.rankingName,
          pendingMatchCount: match.pendingMatchCount,
        });
        navigate(`/rankings/${match.rankingId}/rounds/${match.roundId}`);
      },
    });
  }

  for (const r of subscriber?.wildcardNeededRankings ?? []) {
    todos.push({
      key: `wildcard-${r.roundId}`,
      title: `Definir coringa · rodada ${r.roundNumber}`,
      detail: `${r.rankingName} · seu grupo está incompleto`,
      action: 'Escolher',
      tone: 'neutral',
      onSelect: () => {
        trackEvent('btv_dashboard_wildcard_needed_click', { rankingName: r.rankingName });
        navigate(`/rankings/${r.rankingId}/rounds/${r.roundId}`);
      },
    });
  }

  for (const r of activeRankings) {
    if (r.myStatus === 'pending_payment') {
      todos.push({
        key: `pay-${r.id}`,
        title: 'Pagamento pendente',
        detail: `${r.name} · ${formatBRL(r.paymentAmountCents)} via PIX`,
        action: 'Pagar',
        tone: 'urgent',
        onSelect: () => navigate(`/rankings/${r.id}`),
      });
    } else if (r.myStatus === 'inactive' && r.signupsOpen) {
      // Only once the roster has actually been cleared — otherwise 'inactive'
      // just means "hasn't signed up for a draw that already happened", and
      // there is nothing to confirm.
      todos.push({
        key: `join-${r.id}`,
        title: 'Confirmar presença',
        detail: `${r.name} · fora do sorteio`,
        action: 'Vou jogar',
        tone: 'primary',
        onSelect: () => navigate(`/rankings/${r.id}`),
      });
    }
  }

  /**
   * The notifications opt-in, offered only once there is something to be
   * notified about — before a first ranking it would promise nothing.
   *
   * Deliberately outside `todos`: nothing is being held up, so counting it
   * would keep the header stuck on "1 pendência" for anyone who has not opted
   * in. It has no dismiss — it simply stays until accepted, and disappears on
   * its own once the browser permission is denied, since nothing in the app
   * can undo that.
   */
  const pushTodo: Todo | null =
    push.isSupported && !push.isSubscribed && push.permission !== 'denied' && activeRankings.length > 0
      ? {
          key: 'push-optin',
          title: 'Ativar notificações',
          detail: 'Saiba na hora quando a rodada abrir',
          action: 'Ativar',
          tone: 'neutral',
          icon: Bell,
          onSelect: () => setShowPushDialog(true),
        }
      : null;

  // One reassurance line, so a settled entry fee does not read as an open item.
  const settled = activeRankings.find((r) => r.paymentEnabled && r.myStatus === 'active');
  const hasContent = activeRankings.length > 0 || competitions.length > 0;

  return (
    <AppShell
      headerRightElement={
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 text-white/70 hover:text-turquoise hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
          aria-label="Atualizar"
        >
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      }
    >
      <div className="space-y-4 max-w-lg mx-auto">
        {/* Greeting + pendências summary */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between gap-3"
        >
          <h1 className="font-display text-[22px] font-bold text-white truncate">
            {firstName ? `Fala, ${firstName}` : 'Início'}
          </h1>
          {todos.length > 0 ? (
            <span className="shrink-0 inline-flex items-center gap-2 bg-coral/[.12] border border-coral/30 rounded-full px-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-coral" />
              <span className="text-[13px] font-semibold text-[#FF9B9B]">
                {todos.length} pendência{todos.length === 1 ? '' : 's'}
              </span>
            </span>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-2 bg-success-green/10 border border-success-green/30 rounded-full px-3 py-1.5">
              <Check className="w-3.5 h-3.5 text-success-green" strokeWidth={3} />
              <span className="text-[13px] font-semibold text-success-green">Tudo em dia</span>
            </span>
          )}
        </motion.div>

        {/* Pendências */}
        {(todos.length > 0 || pushTodo || settled) && (
          <div className="glass-card rounded-[18px] overflow-hidden">
            {todos.map((todo, index) => (
              <TodoRow key={todo.key} todo={todo} index={index} />
            ))}
            {pushTodo && <TodoRow todo={pushTodo} index={todos.length} />}
            {settled && (
              <div className="px-4 py-3 flex items-center gap-2.5 bg-success-green/[.06]">
                <Check className="w-4 h-4 text-success-green shrink-0" strokeWidth={3} />
                <span className="text-[13px] text-white/55 truncate">
                  Inscrição em {settled.name} confirmada
                </span>
              </div>
            )}
          </div>
        )}

        {isLoading && <div className="glass-card rounded-2xl p-4 animate-pulse h-[132px]" />}

        {/* Rankings ativos */}
        {!isLoading && activeRankings.length > 0 && (
          <div className={didRefresh ? 'animate-data-refresh rounded-xl' : ''}>
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-[13px] font-semibold text-white/50 uppercase tracking-[.04em]">
                Rankings ativos
              </h2>
              <button
                onClick={() => navigate('/rankings')}
                className="text-[13px] font-semibold text-turquoise hover:text-turquoise/80 transition-colors"
              >
                ver todos
              </button>
            </div>
            <div className="flex flex-col gap-2.5">
              {activeRankings.slice(0, 3).map((ranking) => (
                <RankingCard
                  key={ranking.id}
                  ranking={ranking}
                  onOpen={() => navigate(`/rankings/${ranking.id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Estatísticas */}
        {!isLoading && hasContent && (
          <button
            onClick={() => navigate('/stats')}
            className="w-full flex items-center justify-center gap-2 border border-turquoise/50 text-turquoise font-bold text-sm py-3.5 rounded-xl hover:bg-turquoise/10 transition-colors"
          >
            <BarChart3 className="w-[18px] h-[18px]" />
            Ver minhas estatísticas
          </button>
        )}

        {/* Entrar com código */}
        {showJoin ? (
          <div className="flex flex-row gap-2">
            <input
              type="text"
              autoFocus
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleJoin();
                if (e.key === 'Escape') setShowJoin(false);
              }}
              placeholder="Código de convite"
              maxLength={RANKING_LIMITS.INVITE_CODE_LENGTH}
              className="text-sm flex-1 min-w-0 px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-turquoise/50 font-mono text-center tracking-widest uppercase"
            />
            <Button onClick={handleJoin} size="sm" variant="secondary">
              <LogIn className="w-4 h-4" />
              Entrar
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setShowJoin(true)}
            className="w-full flex items-center justify-center gap-2 border border-dashed border-white/20 rounded-[14px] py-3.5 text-[13px] text-white/55 hover:text-white hover:border-white/35 transition-colors"
          >
            <LogIn className="w-4 h-4 text-turquoise" />
            Entrar em um ranking com código
          </button>
        )}

        {/* Torneios */}
        {competitions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-[13px] font-semibold text-white/50 uppercase tracking-[.04em]">
                Torneios
              </h2>
              <button
                onClick={() => navigate('/tournaments')}
                className="text-[13px] font-semibold text-turquoise hover:text-turquoise/80 transition-colors"
              >
                ver todos
              </button>
            </div>
            <div className="flex flex-col gap-2.5">
              {competitions.slice(0, 2).map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/tournaments/${c.id}`)}
                  className="w-full glass-card rounded-2xl p-4 text-left hover:bg-white/[.06] transition-colors flex items-center gap-3"
                >
                  <CompetitionImage imageUrl={c.imageUrl} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[15px] text-white truncate">{c.name}</p>
                    <p className="text-xs text-white/50">
                      {c.playerCount} jogador{c.playerCount === 1 ? '' : 'es'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/35 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !hasContent && (
          <Card>
            <div className="text-center py-2">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-white/5 flex items-center justify-center">
                <Trophy className="w-7 h-7 text-white/30" />
              </div>
              <h3 className="font-semibold text-white mb-1.5">Comece por aqui</h3>
              <p className="text-sm text-white/50 mb-4">
                Crie um ranking e convide seus amigos, ou entre em um com o código que te
                mandaram.
              </p>
              <Button onClick={() => navigate('/rankings?create=1')} fullWidth>
                Criar meu ranking
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Confirmation in front of the browser's one-shot permission prompt */}
      <AnimatePresence>
        {showPushDialog && (
          <PushOptInDialog
            onClose={() => setShowPushDialog(false)}
            onSubscribe={push.subscribe}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
}
